/**
 * Unified Project View - All Projects (Production Section)
 *
 * Consistent layout with PA View and Assembly View:
 * - Header with title and filters
 * - Kanban overview showing project distribution
 * - Two-column layout: 65% sections + 35% right panel
 * - Collapsible sections by canonical stage
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LAYOUT,
  QueueHeader,
  KanbanOverview,
  Section,
  EmptyRow,
  ProjectRow,
  RightPanel,
  RightPanelCard,
  RightPanelPlaceholder,
  SearchInput,
  PMFilter,
  RefreshButton,
  LastUpdated,
} from './queues/shared/QueueLayout';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Stage configuration matching canonical stages
const STAGES = [
  { id: 'PENDING', label: 'Pending', color: 'PENDING' },
  { id: 'PROD_ART', label: 'Prod Art', color: 'PROD_ART' },
  { id: 'PRINTING', label: 'Printing', color: 'PRINTING' },
  { id: 'ASSEMBLY', label: 'Assembly', color: 'ASSEMBLY' },
  { id: 'SHIPPING', label: 'Shipping', color: 'SHIPPING' },
  { id: 'COMPLETE', label: 'Complete', color: 'COMPLETE' },
];

// Stages shown in Kanban overview (excludes COMPLETE)
const KANBAN_STAGES = STAGES.filter(s => s.id !== 'COMPLETE');

/**
 * Main Unified Project View component
 */
export default function UnifiedProjectView({ token, onSelectProject, employeeId, employeeName }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [columns, setColumns] = useState({});
  const [summary, setSummary] = useState({ total_quotes: 0, total_jobs: 0 });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPM, setSelectedPM] = useState('');

  // Expandable row state
  const [expandedId, setExpandedId] = useState(null);
  const [loadingJobsFor, setLoadingJobsFor] = useState(null);
  const [loadedJobs, setLoadedJobs] = useState({});

  // Selected project for right panel
  const [selectedProject, setSelectedProject] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const authToken = token || sessionStorage.getItem('msal.backendToken');
      if (!authToken) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE}/api/unified/board`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }

      const data = await response.json();
      setColumns(data.columns || {});
      setSummary(data.summary || { total_quotes: 0, total_jobs: 0 });
      setLastUpdated(new Date(data.last_updated));
    } catch (err) {
      console.error('Error fetching unified view:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Extract unique PMs
  const allPMs = useMemo(() => {
    const pms = new Set();
    Object.values(columns).forEach((projects) => {
      projects.forEach((p) => p.pm && pms.add(p.pm));
    });
    return Array.from(pms).sort();
  }, [columns]);

  // Filter projects
  const filteredColumns = useMemo(() => {
    const filtered = {};
    STAGES.forEach(({ id }) => {
      let projects = columns[id] || [];

      if (selectedPM) {
        projects = projects.filter((p) => p.pm === selectedPM);
      }

      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        projects = projects.filter((p) =>
          p.quote_number?.toLowerCase().includes(q) ||
          (p.project_name && p.project_name.toLowerCase().includes(q))
        );
      }

      filtered[id] = projects;
    });
    return filtered;
  }, [columns, selectedPM, searchQuery]);

  // Calculate totals
  const totals = useMemo(() => {
    let projects = 0;
    let jobs = 0;
    let rush = 0;
    Object.values(filteredColumns).forEach((list) => {
      projects += list.length;
      jobs += list.reduce((sum, p) => sum + (p.total_job_count || 0), 0);
      rush += list.filter((p) => p.has_rush).length;
    });
    return { projects, jobs, rush };
  }, [filteredColumns]);

  // Stage counts for summary
  const stageCounts = useMemo(() => {
    return STAGES.map((s) => ({
      ...s,
      count: (filteredColumns[s.id] || []).length,
    }));
  }, [filteredColumns]);

  // Lazy load jobs for a project
  const fetchJobsForProject = useCallback(async (quoteNumber) => {
    if (!quoteNumber || loadedJobs[quoteNumber]) return;

    setLoadingJobsFor(quoteNumber);
    try {
      const authToken = token || sessionStorage.getItem('msal.backendToken');
      const response = await fetch(`${API_BASE}/api/v2/production/projects/${encodeURIComponent(quoteNumber)}/jobs`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLoadedJobs(prev => ({ ...prev, [quoteNumber]: data.jobs || [] }));
      }
    } catch (err) {
      console.error(`Error fetching jobs for ${quoteNumber}:`, err);
    } finally {
      setLoadingJobsFor(null);
    }
  }, [token, loadedJobs]);

  const toggleExpand = (id, quoteNumber) => {
    const newExpandedId = expandedId === id ? null : id;
    setExpandedId(newExpandedId);
    // Lazy load jobs when expanding
    if (newExpandedId && quoteNumber && !loadedJobs[quoteNumber]) {
      fetchJobsForProject(quoteNumber);
    }
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    // Also call parent callback if provided
    onSelectProject?.(project);
  };

  if (loading && Object.keys(columns).length === 0) {
    return (
      <div className={LAYOUT.pageClass}>
        <div className="flex items-center justify-center h-64 text-gray-500">
          Loading projects...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={LAYOUT.pageClass}>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="text-red-600">Error: {error}</div>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={LAYOUT.pageClass}>
      {/* Header - FULL WIDTH */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900">All Projects</h1>
            <p className="text-sm text-gray-500">{totals.projects} projects / {totals.jobs} SKUs{totals.rush > 0 ? ` (${totals.rush} rush)` : ''}</p>
          </div>
          <div className="flex items-center gap-3">
            <SearchInput value={searchQuery} onChange={setSearchQuery} />
            <PMFilter pms={allPMs} selected={selectedPM} onChange={setSelectedPM} />
            <RefreshButton onClick={fetchData} loading={loading} />
            <LastUpdated timestamp={lastUpdated} />
          </div>
        </div>
      </div>

      {/* Kanban Overview - FULL WIDTH (excludes COMPLETE) */}
      <div className="px-4 pb-4">
        <KanbanOverview
          stages={KANBAN_STAGES}
          data={filteredColumns}
          onCardClick={handleProjectClick}
          workflowLabel="Project Workflow"
        />
      </div>

      {/* Sections - constrained width */}
      <div className={LAYOUT.contentClass}>
        {/* Two-column layout */}
        <div className={`flex ${LAYOUT.columnGap}`}>
          {/* Main Queue List */}
          <div className={`${LAYOUT.mainColWidth} ${LAYOUT.sectionGap}`}>
            {STAGES.map(({ id, label, color }) => (
              <Section
                key={id}
                title={label}
                count={(filteredColumns[id] || []).length}
                color={color}
                defaultCollapsed={true}
              >
                {(filteredColumns[id] || []).length === 0 ? (
                  <EmptyRow>No projects in {label.toLowerCase()}</EmptyRow>
                ) : (
                  <ExpandableProjectList
                    projects={filteredColumns[id] || []}
                    expandedId={expandedId}
                    onToggle={toggleExpand}
                    onSelectProject={handleProjectClick}
                    loadedJobs={loadedJobs}
                    loadingJobsFor={loadingJobsFor}
                  />
                )}
              </Section>
            ))}

            {totals.projects === 0 && (
              <div className="text-center py-12 text-gray-500">
                No projects found
              </div>
            )}
          </div>

          {/* Right Panel */}
          <RightPanel>
            {/* Selected Project Details */}
            {selectedProject ? (
              <SelectedProjectCard
                project={selectedProject}
                jobs={loadedJobs[selectedProject.quote_number || selectedProject.quoteNumber] || []}
                onClose={() => setSelectedProject(null)}
              />
            ) : (
              <RightPanelPlaceholder
                title="Project Details"
                description="Click a project to view details"
              />
            )}

            {/* Stage Summary */}
            <RightPanelCard title="Stage Summary">
              <div className="space-y-2">
                {stageCounts.map((stage) => (
                  <div key={stage.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{stage.label}</span>
                    <span className="font-semibold text-gray-900">{stage.count}</span>
                  </div>
                ))}
                <div className="border-t pt-2 mt-2 flex items-center justify-between text-sm font-semibold">
                  <span className="text-gray-700">Total</span>
                  <span className="text-purple-600">{totals.projects}</span>
                </div>
              </div>
            </RightPanelCard>
          </RightPanel>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   EXPANDABLE PROJECT LIST
───────────────────────────────────────────────────────────────────────────── */

function ExpandableProjectList({ projects, expandedId, onToggle, onSelectProject, loadedJobs, loadingJobsFor }) {
  return (
    <div className="divide-y divide-gray-200/50">
      {/* Header row */}
      <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs text-gray-500 font-medium bg-white/80 border-b border-gray-100">
        <div className="col-span-2">Quote ID</div>
        <div className="col-span-4">Project Name</div>
        <div className="col-span-2 text-center">PM</div>
        <div className="col-span-1 text-center"># SKUs</div>
        <div className="col-span-1 text-center">Qty</div>
        <div className="col-span-2 text-right">Ship Date</div>
      </div>
      {/* Project rows */}
      {projects.map((project) => {
        const id = project.id || project.quote_number;
        const quoteNumber = project.quote_number || project.quoteNumber;
        return (
          <ExpandableProjectRow
            key={id}
            project={project}
            isExpanded={expandedId === id}
            onToggle={() => onToggle(id, quoteNumber)}
            onSelectProject={onSelectProject}
            lazyJobs={loadedJobs?.[quoteNumber]}
            loadingJobs={loadingJobsFor === quoteNumber}
          />
        );
      })}
    </div>
  );
}

function ExpandableProjectRow({ project, isExpanded, onToggle, onSelectProject, lazyJobs, loadingJobs }) {
  const quoteNumber = project.quote_number || project.quoteNumber;
  const projectName = project.project_name || project.projectName;
  const pm = project.pm;
  const jobCount = project.total_job_count || project.jobCount || 0;
  const qty = project.total_qty || project.totalQty || 0;
  const isRush = project.has_rush || project.isRush;
  const shipDate = project.ship_date_formatted || project.shipDate;
  const isOverdue = project.days_until_due !== null && project.days_until_due < 0;
  const hasIncompleteMilestones = project.has_incomplete_milestones;
  const jobs = lazyJobs || [];

  return (
    <div>
      {/* Main row */}
      <div
        onClick={onToggle}
        className={`grid grid-cols-12 gap-2 px-4 py-3 items-center cursor-pointer transition-colors hover:bg-white/50 ${
          isExpanded ? "bg-white/30" : ""
        } ${isRush ? "bg-red-50 border-l-4 border-l-red-500" : ""}`}
      >
        {/* Quote Number + Rush/Warning indicators */}
        <div className="col-span-2 flex items-center gap-1.5">
          {isRush && (
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0 animate-pulse" title="Rush" />
          )}
          {hasIncompleteMilestones && (
            <span className="text-amber-500 flex-shrink-0" title="Incomplete milestones - needs investigation">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </span>
          )}
          <span className="font-mono text-xs text-purple-600 font-medium">{quoteNumber || "—"}</span>
        </div>

        {/* Project Name */}
        <div className="col-span-4">
          <span className="text-sm text-gray-900 truncate block font-medium">{projectName || "Untitled"}</span>
        </div>

        {/* PM */}
        <div className="col-span-2 text-center">
          <span className="text-xs text-gray-700">{pm || "—"}</span>
        </div>

        {/* Job Count */}
        <div className="col-span-1 text-center">
          <span className="text-sm text-blue-600 font-semibold">{jobCount}</span>
        </div>

        {/* Total Qty */}
        <div className="col-span-1 text-center">
          <span className="text-sm text-indigo-600 font-semibold">{qty?.toLocaleString() || 0}</span>
        </div>

        {/* Ship Date + expand indicator */}
        <div className="col-span-2 text-right flex items-center justify-end gap-2">
          {shipDate ? (
            <span className={`text-xs font-medium ${isOverdue ? 'text-red-600' : 'text-gray-600'}`}>
              {shipDate}
            </span>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
          <span className="text-gray-400 text-[10px]">{isExpanded ? "▲" : "▼"}</span>
        </div>
      </div>

      {/* Expanded job details */}
      {isExpanded && (
        <div className="bg-white border-t border-gray-200 px-4 py-3">
          <div className="ml-4">
            {/* Action buttons */}
            <div className="flex gap-2 mb-3">
              {onSelectProject && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectProject(project);
                  }}
                  className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-medium flex items-center gap-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Details
                </button>
              )}
            </div>

            {/* Jobs table (lazy loaded) */}
            {loadingJobs ? (
              <div className="flex items-center gap-2 py-4 text-xs text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500" />
                Loading SKU details...
              </div>
            ) : jobs.length > 0 ? (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500">
                    <th className="text-left py-1 font-medium">Job #</th>
                    <th className="text-left py-1 font-medium">SKU</th>
                    <th className="text-left py-1 font-medium">Description</th>
                    <th className="text-center py-1 font-medium">Qty</th>
                    <th className="text-center py-1 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  {jobs.map((job, i) => (
                    <tr key={job.id || job.jobNumber || i} className="border-t border-gray-200/30">
                      <td className="py-1.5 font-mono text-purple-600/70">{job.jobNumber}</td>
                      <td className="py-1.5 font-mono text-gray-500">{job.sku}</td>
                      <td className="py-1.5 truncate max-w-xs">{job.description}</td>
                      <td className="py-1.5 text-center">{job.qty?.toLocaleString()}</td>
                      <td className="py-1.5 text-center">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          job.status === 'Complete' ? 'bg-green-500/20 text-green-600' :
                          job.status === 'Active' ? 'bg-blue-500/20 text-blue-600' :
                          'bg-slate-200/20 text-gray-500'
                        }`}>
                          {job.status || 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-xs text-gray-500">No SKU details available - click to load from JobBoss</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SELECTED PROJECT CARD (Right Panel)
───────────────────────────────────────────────────────────────────────────── */

function SelectedProjectCard({ project, jobs, onClose }) {
  const quoteNumber = project.quote_number || project.quoteNumber;
  const projectName = project.project_name || project.projectName;
  const pm = project.pm;
  const jobCount = project.total_job_count || project.jobCount || 0;
  const qty = project.total_qty || project.totalQty || 0;
  const isRush = project.has_rush || project.isRush;
  const shipDate = project.ship_date_formatted || project.shipDate;
  const stage = project.canonical_stage || project.stage;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-semibold text-purple-600">{quoteNumber}</span>
            {isRush && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded">RUSH</span>
            )}
          </div>
          <p className="text-sm text-gray-900 font-medium mt-0.5">{projectName || 'Untitled'}</p>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 rounded"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Details */}
      <div className="px-4 py-3 space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500 text-xs">PM</span>
            <p className="font-medium text-gray-900">{pm || '—'}</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Stage</span>
            <p className="font-medium text-gray-900">{stage || '—'}</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">SKUs</span>
            <p className="font-medium text-blue-600">{jobCount}</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Total Qty</span>
            <p className="font-medium text-indigo-600">{qty?.toLocaleString() || 0}</p>
          </div>
          <div className="col-span-2">
            <span className="text-gray-500 text-xs">Ship Date</span>
            <p className="font-medium text-gray-900">{shipDate || '—'}</p>
          </div>
        </div>

        {/* Jobs preview */}
        {jobs.length > 0 && (
          <div className="border-t border-gray-100 pt-3">
            <span className="text-gray-500 text-xs">SKUs ({jobs.length})</span>
            <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
              {jobs.slice(0, 5).map((job, i) => (
                <div key={job.id || i} className="flex items-center justify-between text-xs">
                  <span className="font-mono text-gray-600">{job.sku || job.jobNumber}</span>
                  <span className="text-gray-500">{job.qty?.toLocaleString()}</span>
                </div>
              ))}
              {jobs.length > 5 && (
                <p className="text-xs text-gray-400">+{jobs.length - 5} more</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
